import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { FaBold, FaItalic, FaUnderline, FaStrikethrough, FaCode, FaLink, FaUnlink, FaListUl, FaListOl, FaQuoteRight, FaImage, FaUndo, FaRedo, FaEraser, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaMinus, FaTrash, FaArrowUp, FaArrowDown, FaArrowsAlt } from 'react-icons/fa';
import { FieldSelect } from '../../Form/_Fields/FieldSelect';

const BLOCK = 'LStudio__app_rte';

/*
    Imagen administrable para los editores que lo pidan explícitamente.
    Conserva el comportamiento normal de TipTap en el resto de NOTEBOOK.
*/
const ManagedImage = Image.extend({
    draggable: true,
    addAttributes() {
        return {
            ...this.parent?.(),
            align: {
                default: 'left',
                parseHTML: (element) => element.getAttribute('data-align') || 'left',
                renderHTML: (attributes) => ({ 'data-align': attributes.align || 'left' }),
            },
        };
    },
});

/*
    🔥 EDITOR — TipTap

    Está construido sobre ProseMirror, el mismo motor que usan Notion,
    Linear y GitLab. La diferencia con Quill:

      · Quill trae su propia barra y su propio CSS. Rápido de poner,
        difícil de personalizar.
      · TipTap es HEADLESS: no dibuja nada. La barra es nuestra, así
        que respeta los tokens sin pelear con CSS de terceros.

    Por eso este archivo es más largo: la barra la escribimos nosotros.

    npm i @tiptap/react @tiptap/starter-kit @tiptap/extension-link
          @tiptap/extension-underline @tiptap/extension-text-align
          @tiptap/extension-placeholder @tiptap/extension-image
*/

const ToolButton = ({ onClick, isActive, isDisabled, title, children }) => (
    <button type="button" className={`${BLOCK}__tool`} data-on={isActive}
        title={title} aria-label={title} disabled={isDisabled}
        onMouseDown={(event) => event.preventDefault()}   /* no perder el foco del editor */
        onClick={onClick} >
        {children}
    </button>
);

export const RichTextTipTap = ({
    value, onChange, placeholder = 'Escribí acá…',
    isDisabled = false, minHeight = 160,
    enableImageControls = false,
}) => {

    const editor = useEditor({
        editable: !isDisabled,
        content: value ?? '',
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            (enableImageControls ? ManagedImage : Image).configure({ inline: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder }),
        ],
        onUpdate: ({ editor: instance }) => onChange?.(instance.getHTML()),
    });

    /*
        ⚠️ SIN ESTO EL EDITOR SE BORRA SOLO

        TipTap mantiene su propio estado interno. Si el padre cambia
        `value` (un reset del formulario, cargar datos de la API), el
        editor no se entera. Y si sincronizamos sin comparar, cada
        pulsación reescribe el contenido y el cursor salta al inicio.
    */
    useEffect(() => {

        if (!editor) return;

        const CURRENT = editor.getHTML();

        if ((value ?? '') !== CURRENT) {
            editor.commands.setContent(value ?? '', false);
        }

    }, [value, editor]);

    useEffect(() => {
        editor?.setEditable(!isDisabled);
    }, [isDisabled, editor]);

    if (!editor) return null;

    const onSetLink = () => {

        const PREVIOUS = editor.getAttributes('link').href;
        const URL = window.prompt('Dirección del enlace', PREVIOUS ?? 'https://');

        if (URL === null) return;

        if (URL === '') return editor.chain().focus().extendMarkRange('link').unsetLink().run();

        editor.chain().focus().extendMarkRange('link').setLink({ href: URL }).run();
    };

    const onAddImage = () => {
        const URL = window.prompt('Dirección de la imagen', 'https://');
        if (URL) editor.chain().focus().setImage({ src: URL, align: 'left' }).run();
    };

    const selectedImageMeta = (() => {
        if (!enableImageControls || !editor.isActive('image')) return { index: -1, canMoveUp: false, canMoveDown: false };

        const { selection, doc } = editor.state;
        if (!selection.node || selection.node.type?.name !== 'image') return { index: -1, canMoveUp: false, canMoveDown: false };

        const blocks = [];
        doc.forEach((node, offset) => blocks.push({ node, pos: offset }));
        const index = blocks.findIndex((item) => item.pos === selection.from);

        return {
            index,
            canMoveUp: index > 0,
            canMoveDown: index >= 0 && index < blocks.length - 1,
        };
    })();

    const onMoveImage = (direction) => {
        const { state, view } = editor;
        const { selection, doc } = state;
        const imageNode = selection.node;

        if (!imageNode || imageNode.type?.name !== 'image') return;

        const blocks = [];
        doc.forEach((node, offset) => blocks.push({ node, pos: offset }));
        const index = blocks.findIndex((item) => item.pos === selection.from);
        const targetIndex = index + direction;
        if (index < 0 || targetIndex < 0 || targetIndex >= blocks.length) return;

        const currentPos = blocks[index].pos;
        let insertPos;
        const transaction = state.tr.delete(currentPos, currentPos + imageNode.nodeSize);

        if (direction < 0) {
            insertPos = blocks[targetIndex].pos;
        } else {
            insertPos = currentPos + blocks[targetIndex].node.nodeSize;
        }

        transaction.insert(insertPos, imageNode);
        view.dispatch(transaction.scrollIntoView());
        editor.commands.setNodeSelection(insertPos);
        editor.commands.focus();
    };

    const imageAlign = editor.getAttributes('image')?.align || 'left';

    return (
        <div className={`${BLOCK} ${BLOCK}--tiptap`} data-disabled={isDisabled}
            data-image-controls={enableImageControls ? 'true' : 'false'}
            style={{ '--LS__rte_min_height': `${minHeight}px` }} >

            <div className={`${BLOCK}__toolbar`}>

                <div className={`${BLOCK}__group`}>
                    <div className={`${BLOCK}__select`}>
                        <FieldSelect isBare isSearchable={false}
                            value={
                                editor.isActive('heading', { level: 2 }) ? 'h2'
                                    : editor.isActive('heading', { level: 3 }) ? 'h3'
                                        : editor.isActive('heading', { level: 4 }) ? 'h4' : 'p'
                            }
                            options={[
                                { value: 'p', label: 'Normal' },
                                { value: 'h2', label: 'Titulo 1' },
                                { value: 'h3', label: 'Titulo 2' },
                                { value: 'h4', label: 'Titulo 3' },
                            ]}
                            onChange={(event) => {
                                const V = event.target.value;
                                if (V === 'p') return editor.chain().focus().setParagraph().run();
                                editor.chain().focus().toggleHeading({ level: Number(V[1]) }).run();
                            }} />
                    </div>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Negrita" isActive={editor.isActive('bold')}
                        onClick={() => editor.chain().focus().toggleBold().run()}><FaBold /></ToolButton>
                    <ToolButton title="Cursiva" isActive={editor.isActive('italic')}
                        onClick={() => editor.chain().focus().toggleItalic().run()}><FaItalic /></ToolButton>
                    <ToolButton title="Subrayado" isActive={editor.isActive('underline')}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}><FaUnderline /></ToolButton>
                    <ToolButton title="Tachado" isActive={editor.isActive('strike')}
                        onClick={() => editor.chain().focus().toggleStrike().run()}><FaStrikethrough /></ToolButton>
                    <ToolButton title="Código" isActive={editor.isActive('code')}
                        onClick={() => editor.chain().focus().toggleCode().run()}><FaCode /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Lista con viñetas" isActive={editor.isActive('bulletList')}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}><FaListUl /></ToolButton>
                    <ToolButton title="Lista numerada" isActive={editor.isActive('orderedList')}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}><FaListOl /></ToolButton>
                    <ToolButton title="Cita" isActive={editor.isActive('blockquote')}
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}><FaQuoteRight /></ToolButton>
                    <ToolButton title="Separador"
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}><FaMinus /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Izquierda" isActive={editor.isActive({ textAlign: 'left' })}
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}><FaAlignLeft /></ToolButton>
                    <ToolButton title="Centro" isActive={editor.isActive({ textAlign: 'center' })}
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}><FaAlignCenter /></ToolButton>
                    <ToolButton title="Derecha" isActive={editor.isActive({ textAlign: 'right' })}
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}><FaAlignRight /></ToolButton>
                    <ToolButton title="Justificado" isActive={editor.isActive({ textAlign: 'justify' })}
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}><FaAlignJustify /></ToolButton>
                </div>

                <div className={`${BLOCK}__group`}>
                    <ToolButton title="Enlace" isActive={editor.isActive('link')}
                        onClick={onSetLink}><FaLink /></ToolButton>
                    <ToolButton title="Quitar enlace" isDisabled={!editor.isActive('link')}
                        onClick={() => editor.chain().focus().unsetLink().run()}><FaUnlink /></ToolButton>
                    <ToolButton title="Imagen" onClick={onAddImage}><FaImage /></ToolButton>
                </div>

                <div className={`${BLOCK}__group ${BLOCK}__group--end`}>
                    <ToolButton title="Deshacer" isDisabled={!editor.can().undo()}
                        onClick={() => editor.chain().focus().undo().run()}><FaUndo /></ToolButton>
                    <ToolButton title="Rehacer" isDisabled={!editor.can().redo()}
                        onClick={() => editor.chain().focus().redo().run()}><FaRedo /></ToolButton>
                    <ToolButton title="Limpiar formato"
                        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><FaEraser /></ToolButton>
                </div>

            </div>

            {enableImageControls && editor.isActive('image') && (
                <div className={`${BLOCK}__imagebar`} role="toolbar" aria-label="Acciones de imagen">
                    <span className={`${BLOCK}__imagebar_label`}><FaArrowsAlt /> Imagen seleccionada</span>
                    <div className={`${BLOCK}__imagebar_actions`}>
                        <ToolButton title="Mover arriba" isDisabled={!selectedImageMeta.canMoveUp}
                            onClick={() => onMoveImage(-1)}><FaArrowUp /></ToolButton>
                        <ToolButton title="Mover abajo" isDisabled={!selectedImageMeta.canMoveDown}
                            onClick={() => onMoveImage(1)}><FaArrowDown /></ToolButton>
                        <span className={`${BLOCK}__imagebar_separator`} />
                        <ToolButton title="Alinear a la izquierda" isActive={imageAlign === 'left'}
                            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()}><FaAlignLeft /></ToolButton>
                        <ToolButton title="Centrar imagen" isActive={imageAlign === 'center'}
                            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()}><FaAlignCenter /></ToolButton>
                        <ToolButton title="Alinear a la derecha" isActive={imageAlign === 'right'}
                            onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()}><FaAlignRight /></ToolButton>
                        <span className={`${BLOCK}__imagebar_separator`} />
                        <ToolButton title="Eliminar imagen"
                            onClick={() => editor.chain().focus().deleteSelection().run()}><FaTrash /></ToolButton>
                    </div>
                    <small>También podés arrastrarla para cambiarla de lugar.</small>
                </div>
            )}

            <EditorContent editor={editor} className={`${BLOCK}__content`} />

        </div>
    );
};
